'use client'

import React, { useCallback, useEffect, useState } from 'react'
import ForecastCardCarousel from './ForecastCardCarousel'
import { getLocationManager } from '@/services/location/LocationManagerInstance'
import { filterDayboxes, getCurrentLocalTime, transformDaybox } from './utils'
import { Location } from '@/services/location/types'
import { getCurrentConditionTemperature } from '@/utils/kmm'
import {
  fetchCurrentConditions,
  fetchDayboxes,
} from '@/services/api/bff/weather/Weather.service'
import {
  Daybox,
  DayboxAttributes,
  DayboxHourlyEntry,
} from '@/services/api/bff/weather/dtos/Dayboxes.dto'

export interface ParsedDaybox extends Omit<Daybox, 'attributes'> {
  attributes: Omit<DayboxAttributes, 'hourly' | 'sunrise' | 'sunset'> & {
    sunrise: string
    sunset: string
    hourly: (DayboxHourlyEntry & {
      temp: string
      isNow: boolean
      localTime: string
      localHour: number
      localDay: number
      localMonth: number
      localDateTime: string
    })[]
  }
  description: string
  isToday: boolean
  weekDay: string
}

export interface ActivePlace {
  name: string
  url: string
  limitedDayboxes: ParsedDaybox[]
  dayDescription: string
  currentTemp: string
  currentFeltTemp: string
  currentSymbol: string
  localTime: string
  localHour: number
  localDateTime: string
}

const ForecastCardCarouselState: React.FC = () => {
  const locationManager = getLocationManager()
  const [cards, setCards] = useState<any[]>([])

  /**
   * Fetching the dayboxes and map data to an usable format
   *
   * TODO refactor this to use new api endpoints and to be more performant (SSR)
   */
  const fetchDayboxesCallback = useCallback(
    async (loc: Location): Promise<ActivePlace | null> => {
      try {
        const currentConditionsFetch = fetchCurrentConditions(loc.geohash)
        const dayboxesFetch = fetchDayboxes(loc.geohash)

        const [currentConditionsResponse, dayboxesResponse] = await Promise.all(
          [currentConditionsFetch, dayboxesFetch],
        )
        const currentConditions = currentConditionsResponse?.data?.attributes
        const dayboxes = dayboxesResponse?.data

        if (!dayboxes?.length || !currentConditions) return null

        const tz = loc.timezone as string
        const nowInfo = getCurrentLocalTime(tz)
        const nowIso = nowInfo.dateToHour

        const filtered = filterDayboxes(dayboxes, tz, nowIso)

        const parsed = filtered.map((db: Daybox, idx: number) =>
          transformDaybox(db, idx, tz, nowInfo),
        ) as ParsedDaybox[]

        const currentTemp = currentConditions.temperature.toFixed()
        const currentFelt = getCurrentConditionTemperature(
          parseFloat(currentTemp),
          currentConditions.tApparent,
        )
        const currentSymbol = currentConditions.weatherSymbol

        return {
          name: loc.name,
          url: loc.url,
          limitedDayboxes: parsed,
          dayDescription: parsed[0].description,
          currentTemp,
          currentFeltTemp: currentFelt,
          currentSymbol,
          localTime: nowInfo.time,
          localHour: nowInfo.hour,
          localDateTime: nowInfo.dateToHour,
        }
      } catch {
        return null
      }
    },
    [],
  )

  const refresh = useCallback(() => {
    const topFive = locationManager.getTopFiveLocations()
    Promise.all(topFive.map(fetchDayboxesCallback)).then((results) =>
      setCards(results.filter((r): r is ActivePlace => !!r)),
    )
  }, [locationManager, fetchDayboxesCallback])

  useEffect(() => {
    refresh()
    return locationManager.subscribe(refresh)
  }, [refresh, locationManager])

  return <ForecastCardCarousel cards={cards} />
}
export default ForecastCardCarouselState
